import { Collapse, Dialog, DialogContent, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsSelect, FoodsTextInput, Textarea, Typography, WarningBox } from "@foodpilot/foods";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SsoLoginMode, SsoProtocol, SSO_PROTOCOLS } from "src/pages/Authentication/ssoConnector";
import { BaseFooter } from "src/components/Dialog/Footers/BaseFooter";
import { BaseHeader } from "src/components/Dialog/Headers/BaseHeader";
import { useSnackbar } from "src/utils/useSnackbar";
import {
    buildProviderCallbackUrl,
    carryOverModeValues,
    type ExistingProvider,
    PROVIDER_PROTOCOL_MODE_LABELS,
    PROVIDER_PROTOCOLS,
    type ProviderProtocol,
    readMode,
    TRANSITIONAL_PROVIDER_PROTOCOL,
    getGroupFields,
    getProviderGroups,
    LABEL_FIELD_KEY,
    isAcsUrlMismatched,
    OAUTH_SECRET_FIELD_KEY,
    PROVIDER_PROTOCOL_LABELS,
    ProviderFieldConfig,
    ProviderFieldGroup,
    ProviderFormValues,
    providerToFormValues,
    SAML2_ACS_FIELD_KEY,
    validateProviderForm,
} from "../ssoProviderModel";
import { CallbackUrlPanel } from "./CallbackUrlPanel";
import { ProviderButtonFields, ProviderButtonValues } from "./ProviderButtonFields";
import { Field } from "./SsoSettingsFields";
import { useProviderFieldCopy } from "./useProviderFieldCopy";

export type ProviderDraft = {
    button: ProviderButtonValues;
    protocol: SsoProtocol;
    values: ProviderFormValues;
};

type SsoProviderFormDialogProps = {
    apiOrigin: string;
    existingProviders: ExistingProvider[];
    initialProtocol: SsoProtocol;
    isSaving: boolean;
    loginMode: SsoLoginMode;
    onClose: () => void;
    onSubmit: (draft: ProviderDraft) => Promise<void>;
    open: boolean;
    provider?: Record<string, unknown>;
    serverIssues?: Record<string, string>;
    storedLogo: string | undefined;
};

type ChoiceOption = { id: string; label: string };

const MODE_OPTIONS: ChoiceOption[] = PROVIDER_PROTOCOLS.map((mode) => ({
    id: mode,
    label: PROVIDER_PROTOCOL_MODE_LABELS[mode],
}));

const PROTOCOL_OPTIONS: ChoiceOption[] = SSO_PROTOCOLS.map((protocol) => ({
    id: protocol,
    label: PROVIDER_PROTOCOL_LABELS[protocol],
}));

export const SsoProviderFormDialog = (props: SsoProviderFormDialogProps) => {
    const {
        apiOrigin,
        existingProviders,
        initialProtocol,
        isSaving,
        loginMode,
        onClose,
        onSubmit,
        open,
        provider,
        serverIssues,
        storedLogo,
    } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const copy = useProviderFieldCopy();
    const isEditing = provider !== undefined;

    const [protocol, setProtocol] = useState<SsoProtocol>(initialProtocol);
    const [values, setValues] = useState<ProviderFormValues>(() => providerToFormValues(initialProtocol, provider));
    const [button, setButton] = useState<ProviderButtonValues>({
        selectedLogo: null,
        isLogoCleared: false,
    });
    const [isAdvancedOpen, setAdvancedOpen] = useState(false);
    const [touched, setTouched] = useState<Set<string>>(new Set());

    const setField = (key: string) => (value: string) => {
        setValues((previous) => ({ ...previous, [key]: value }));
        setTouched((previous) => new Set(previous).add(key));
    };

    const originalName = isEditing ? String(provider?.providerName ?? "") : undefined;
    const issues = validateProviderForm({
        currentName: originalName,
        existingProviders,
        isCreate: !isEditing,
        protocol,
        values,
    });
    const isValid = Object.keys(issues).length === 0;

    const submit = () => {
        if (!isValid) {
            setTouched(new Set(Object.keys(issues)));
            return;
        }

        void onSubmit({ button, protocol, values });
    };

    const providerName = values.providerName ?? "";
    const mode = readMode(values);
    const callbackUrl = buildProviderCallbackUrl(protocol, providerName, apiOrigin);
    const acsMismatched = protocol === "saml2" && isAcsUrlMismatched(values, callbackUrl);

    const changeMode = (next: ProviderProtocol) => {
        setValues((previous) => carryOverModeValues(previous, next));
        setTouched((previous) => new Set([...previous].filter((key) => key === "providerName")));
    };

    const changeProtocol = (next: SsoProtocol) => {
        setProtocol(next);
        setValues((previous) => ({ ...providerToFormValues(next), providerName: previous.providerName ?? "" }));
        setTouched((previous) => new Set([...previous].filter((key) => key === "providerName")));
    };

    const copyCallbackUrl = () => {
        navigator.clipboard
            .writeText(callbackUrl)
            .then(() =>
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("settings.sso.providers.callback_url_copied", "Callback URL copied"),
                }),
            )
            .catch(() => snackbar.setSnackbarProps({ type: "error", message: t("common.error_occurred") }));
    };

    const fieldError = (key: string): string | undefined => {
        const issue = issues[key];
        if (issue !== undefined && touched.has(key)) return copy.issues[issue];

        return serverIssues?.[key];
    };

    const renderControl = (field: ProviderFieldConfig, label: string, isLocked: boolean) => {
        if (field.kind === "textarea" || field.kind === "keyvalue") {
            return (
                <Textarea
                    value={values[field.key] ?? ""}
                    onChange={setField(field.key)}
                    placeholder={label}
                    minRows={field.kind === "textarea" ? 4 : 3}
                    error={fieldError(field.key) !== undefined}
                />
            );
        }

        return (
            <FoodsTextInput
                value={values[field.key] ?? ""}
                onChange={setField(field.key)}
                disabled={isLocked}
                ExtraInputProps={{ inputProps: { "aria-label": label } }}
            />
        );
    };

    const renderField = (field: ProviderFieldConfig) => {
        const isLocked = field.lockedOnEdit === true && isEditing;
        const { label, help } = copy.fields[field.key] ?? { label: field.key };
        const error = fieldError(field.key);
        const isWide = field.kind === "textarea" || field.kind === "keyvalue";

        return (
            <Field key={field.key} label={label} fullWidth={isWide}>
                {renderControl(field, label, isLocked)}
                {help !== undefined && !isLocked && (
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                        {help}
                    </Typography>
                )}
                {isLocked && (
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                        {t(
                            "settings.sso.providers.name_locked_help",
                            "The provider name cannot be changed after creation.",
                        )}
                    </Typography>
                )}
                {field.kind === "keyvalue" && (
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                        {t("settings.sso.providers.keyvalue_help", "One per line, formatted as key: value.")}
                    </Typography>
                )}
                {field.kind === "list" && (
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                        {t("settings.sso.providers.list_help", "Comma-separated values.")}
                    </Typography>
                )}
                {field.key === SAML2_ACS_FIELD_KEY && acsMismatched && (
                    <Stack flexDirection="row" alignItems="center" gap={theme.spacing(1)} flexWrap="wrap">
                        <Typography variant="small-medium" sx={{ color: theme.palette.warning.dark }}>
                            {t(
                                "settings.sso.providers.acs_mismatch",
                                "This differs from the URL this application actually serves.",
                            )}
                        </Typography>
                        <Button variant="ternary" onClick={() => setField(SAML2_ACS_FIELD_KEY)(callbackUrl)}>
                            {t("settings.sso.providers.acs_use_expected", "Use the served URL")}
                        </Button>
                    </Stack>
                )}
                {error !== undefined && (
                    <Typography variant="small-medium" sx={{ color: theme.palette.error.main }}>
                        {error}
                    </Typography>
                )}
            </Field>
        );
    };

    const renderSecretField = () => {
        const label = copy.fields[OAUTH_SECRET_FIELD_KEY].label;
        const error = fieldError(OAUTH_SECRET_FIELD_KEY);

        return (
            <Field label={label}>
                <FoodsTextInput
                    value={values[OAUTH_SECRET_FIELD_KEY] ?? ""}
                    onChange={setField(OAUTH_SECRET_FIELD_KEY)}
                    ExtraInputProps={{ type: "password", inputProps: { "aria-label": label } }}
                />
                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                    {isEditing ?
                        t("settings.sso.providers.secret_new_help", "Only fill this in to set or replace the secret.")
                    :   t("settings.sso.providers.secret_create_help", "Stored encrypted and never shown again.")}
                </Typography>
                {error !== undefined && (
                    <Typography variant="small-medium" sx={{ color: theme.palette.error.main }}>
                        {error}
                    </Typography>
                )}
            </Field>
        );
    };

    const renderGroupFields = (group: ProviderFieldGroup) => (
        <Stack flexDirection="row" flexWrap="wrap" gap={theme.spacing(2)}>
            {getGroupFields(protocol, group, mode).map(renderField)}
            {group === "credentials" && protocol === "oauth" && renderSecretField()}
        </Stack>
    );

    const renderFlavourField = () => (
        <Field label={t("settings.sso.providers.mode_label", "OAuth flavour")}>
            <FoodsSelect
                selectedOption={MODE_OPTIONS.find((option) => option.id === mode) ?? null}
                options={MODE_OPTIONS.filter(
                    (option) => option.id !== TRANSITIONAL_PROVIDER_PROTOCOL || mode === TRANSITIONAL_PROVIDER_PROTOCOL,
                )}
                onChange={(option) => option !== null && changeMode(option.id as ProviderProtocol)}
                getId={(option) => option.id}
                getName={(option) => option.label}
                textOptions={{
                    placeholder: t("settings.sso.providers.mode_label", "OAuth flavour"),
                    searchPlaceholder: t("component.select.searchplaceholder"),
                    emptyOptionsText: t("component.select.nooptions"),
                    defaultValueText: t("component.select.defaultvalue"),
                    noResult: t("component.select.noresultfound"),
                }}
            />
            {mode === TRANSITIONAL_PROVIDER_PROTOCOL && (
                <WarningBox>
                    <Typography variant="body-medium">
                        {t(
                            "settings.sso.providers.transitional_mode_warning",
                            "Transitional: identity comes from a signed access token. Add openid to the scopes and switch to OpenID Connect.",
                        )}
                    </Typography>
                </WarningBox>
            )}
            {mode !== "oauth2" && (
                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                    {t(
                        "settings.sso.providers.endpoints_from_metadata_help",
                        "The authorization, token and user info endpoints are read from the discovery document.",
                    )}
                </Typography>
            )}
        </Field>
    );

    const renderProtocolField = () => (
        <Field label={t("settings.sso.protocol_placeholder", "Protocol")}>
            <FoodsSelect
                selectedOption={PROTOCOL_OPTIONS.find((option) => option.id === protocol) ?? null}
                options={PROTOCOL_OPTIONS}
                onChange={(option) => option !== null && changeProtocol(option.id as SsoProtocol)}
                getId={(option) => option.id}
                getName={(option) => option.label}
                disabled={isEditing}
                textOptions={{
                    placeholder: t("settings.sso.protocol_placeholder", "Protocol"),
                    searchPlaceholder: t("component.select.searchplaceholder"),
                    emptyOptionsText: t("component.select.nooptions"),
                    defaultValueText: t("component.select.defaultvalue"),
                    noResult: t("component.select.noresultfound"),
                }}
            />
            {isEditing && (
                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                    {t("settings.sso.providers.protocol_locked_help", "The protocol cannot be changed after creation.")}
                </Typography>
            )}
        </Field>
    );

    const renderGroup = (group: ProviderFieldGroup) => {
        if (group === "advanced" || group === "button") return null;

        return (
            <Stack key={group} gap={theme.spacing(2)}>
                <Typography variant="body-bold" component="h3">
                    {copy.groups[group]}
                </Typography>
                {group === "identity" && renderProtocolField()}
                {renderGroupFields(group)}
                {group === "identity" && protocol === "oauth" && renderFlavourField()}
            </Stack>
        );
    };

    const groups = getProviderGroups(protocol, mode);
    const hasAdvanced = groups.includes("advanced");

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <BaseHeader
                title={
                    isEditing ?
                        t("settings.sso.providers.edit_title", "Edit {{protocol}} provider", {
                            protocol: PROVIDER_PROTOCOL_LABELS[protocol],
                        })
                    :   t("settings.sso.providers.create_title", "Add a provider")
                }
                closeWindow={onClose}
            />
            <DialogContent sx={{ padding: theme.spacing(4) }}>
                <Stack gap={theme.spacing(4)}>
                    <CallbackUrlPanel callbackUrl={callbackUrl} onCopy={copyCallbackUrl} protocol={protocol} />

                    {groups.map(renderGroup)}

                    <Stack gap={theme.spacing(2)}>
                        <Stack gap={theme.spacing(0.5)}>
                            <Typography variant="body-bold" component="h3">
                                {t("settings.sso.providers.group.button", "Login page button")}
                            </Typography>
                            <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                                {t(
                                    "settings.sso.providers.group.button_help",
                                    "How this provider appears to people signing in.",
                                )}
                            </Typography>
                        </Stack>
                        {renderGroupFields("button")}
                        <ProviderButtonFields
                            label={values[LABEL_FIELD_KEY] ?? ""}
                            loginMode={loginMode}
                            onChange={setButton}
                            protocol={protocol}
                            providerName={providerName}
                            storedLogo={storedLogo}
                            values={button}
                        />
                    </Stack>

                    {hasAdvanced && (
                        <Stack gap={theme.spacing(2)}>
                            <Button
                                variant="ternary"
                                onClick={() => setAdvancedOpen((previous) => !previous)}
                                sx={{ alignSelf: "flex-start" }}
                                startIcon={
                                    <FoodsIcon size={2.5} icon={isAdvancedOpen ? "arrowUpShort" : "arrowDownShort"} />
                                }
                            >
                                {copy.groups.advanced}
                            </Button>
                            <Collapse in={isAdvancedOpen} unmountOnExit>
                                {renderGroupFields("advanced")}
                            </Collapse>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <BaseFooter
                onCancelAction={onClose}
                onConfirmAction={submit}
                canValidate={!isSaving}
                cancelText={t("common.cancel")}
                validateText={t("common.save")}
            />
        </Dialog>
    );
};
